import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getWidgetCategorySearchRedirectOverrides } from 'utils/searchUrlHelpers';
import usePurchase from './usePurchase';

/**
 * Hook to handle redirection to trip search
 * @returns {Object} Object with handleGotoSearch function
 */
const useSearchRedirect = () => {
  const history = useHistory();
  const { features } = useSelector((state) => state.whitelabelConfig);
  const purchase = usePurchase();

  const {
    isExpired,
    departs,
    roundTrip,
    isExchange,
    returns,
    encodedPassengersSelection = 'A1',
  } = purchase;

  const origin = departs?.origin?.id;
  const originCityId = departs?.origin?.cityId;
  const destination = departs?.destination?.id;
  const destinationCityId = departs?.destination?.cityId;
  const returnIsOpenTicket = returns?.fragments?.[0]?.openTicket;
  const departureDate = departs?.departure;
  const returnDate = returns?.departure;

  const needsCityId = features.ENABLE_CITY_ID_IN_SEARCH;

  /**
   * Function to redirect user to search page
   * @param {Object} options - Additional options
   * @param {string} options.searchDepartureDate - Departure date for search in DD-MM-YYYY format
   * @param {string} options.searchReturnsDate - Return date for search in DD-MM-YYYY format
   * @returns {void}
   */
  const handleGotoSearch = useCallback(
    (options = {}) => {
      const { searchDepartureDate, searchReturnsDate } = options;

      if (!origin || !destination) {
        console.error('Error: Origin or destination not available for redirection');
        return;
      }

      const overrides = getWidgetCategorySearchRedirectOverrides();

      let departure;
      if (overrides?.departureDate) {
        // Widget-category undo: use raw URL-format date from search state
        // (e.g. `2026-05-10`) instead of a locale-formatted reformat
        departure = overrides.departureDate;
      } else if (searchDepartureDate) {
        // If searchDepartureDate is provided, use it directly
        departure = moment(searchDepartureDate, 'DD-MM-YYYY').format('DD-MMM-YY');
      } else {
        // Normal case, use departure date from state
        departure = moment(departureDate).format('DD-MMM-YY');
      }

      let url = `/search/${needsCityId ? originCityId : origin}/${
        needsCityId ? destinationCityId : destination
      }/${departure}/`;

      if (roundTrip) {
        let returns;
        if (overrides?.returnDate) {
          returns = overrides.returnDate;
        } else if (searchReturnsDate) {
          // If searchReturnsDate is provided, use it directly
          returns = moment(searchReturnsDate, 'DD-MM-YYYY').format('DD-MMM-YY');
        } else if (returnIsOpenTicket) {
          // If it's an open ticket, use return date from state
          returns = moment(returnDate).format('DD-MMM-YY');
        } else {
          // Normal case, use return date from state
          returns = moment(returnDate).format('DD-MMM-YY');
        }

        url += `${returns}/`;
      }

      if (isExchange) {
        history.push('/exchange');
      } else {
        const finalEncoded = overrides?.encodedPassengers || encodedPassengersSelection;
        const finalQuery = overrides?.querySuffix || '';
        history.push(`${url}p/${finalEncoded}/departures${finalQuery}`);
      }
    },
    [
      departureDate,
      destination,
      destinationCityId,
      encodedPassengersSelection,
      history,
      isExchange,
      needsCityId,
      origin,
      originCityId,
      returnDate,
      returnIsOpenTicket,
      roundTrip,
    ],
  );

  return {
    handleGotoSearch,
    isExpired,
  };
};

export default useSearchRedirect;
